// Tabs — controlled. Recebe value + onChange. <Tabs.Tab id="..." label="..." />.
// A11y: ARIA tablist/tab/tabpanel roles. Setas arrow esquerda/direita pra navegar.
import {
  Children,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import styles from './Tabs.module.css';
import { cn } from '@/lib/utils/cn';

interface TabsContextValue {
  value: string;
  onChange: (id: string) => void;
  baseId: string;
  registerTab: (id: string, el: HTMLButtonElement | null) => void;
  ids: string[];
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error('Tabs.Tab deve ser usado dentro de <Tabs>');
  }
  return ctx;
}

export interface TabsProps {
  value: string;
  onChange: (id: string) => void;
  children: ReactNode;
  className?: string;
  /** rótulo acessível pra a lista (lido por leitores de tela) */
  label: string;
  /**
   * Namespace dos ids gerados (`<prefix>-tab-<id>` / `<prefix>-panel-<id>`).
   *
   * Sem ele o prefixo vem de `useId()` e fica INACESSÍVEL fora deste componente —
   * então o `aria-controls` de cada tab aponta para um painel que o caller não tem
   * como nomear, e o axe reprova com `aria-valid-attr-value` (referência pendurada).
   * Passe o mesmo prefixo aqui e no `id` do seu `role="tabpanel"`.
   */
  idPrefix?: string;
}

export interface TabProps {
  id: string;
  label: string;
  count?: number;
  /** Renderiza o `count` como bolinha no canto superior esquerdo (notificação),
   *  em vez da pílula inline. Usado p/ destacar pendências (ex.: fila). */
  countBadge?: boolean;
  icon?: ReactNode;
}

function Tab({ id, label, count, countBadge, icon }: TabProps) {
  const ctx = useTabsContext();
  const tabRef = useRef<HTMLButtonElement | null>(null);

  const setRef = useCallback(
    (el: HTMLButtonElement | null) => {
      tabRef.current = el;
      ctx.registerTab(id, el);
    },
    [ctx, id],
  );

  const active = ctx.value === id;

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }
    e.preventDefault();
    const idx = ctx.ids.indexOf(id);
    if (idx === -1) return;
    let next = idx;
    if (e.key === 'ArrowRight') next = (idx + 1) % ctx.ids.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + ctx.ids.length) % ctx.ids.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = ctx.ids.length - 1;
    ctx.onChange(ctx.ids[next]);
  };

  return (
    <button
      ref={setRef}
      type="button"
      role="tab"
      id={`${ctx.baseId}-tab-${id}`}
      aria-selected={active}
      aria-controls={`${ctx.baseId}-panel-${id}`}
      tabIndex={active ? 0 : -1}
      data-active={active || undefined}
      className={cn(styles.tab, active && styles.active)}
      onClick={() => ctx.onChange(id)}
      onKeyDown={onKeyDown}
    >
      {typeof count === 'number' && countBadge && <span className={styles.tabBadge}>{count}</span>}
      {icon && <span className={styles.tabIcon}>{icon}</span>}
      <span>{label}</span>
      {typeof count === 'number' && !countBadge && <span className={styles.tabCount}>{count}</span>}
    </button>
  );
}

export function Tabs({ value, onChange, children, className, label, idPrefix }: TabsProps) {
  const generatedId = useId();
  const baseId = idPrefix ?? generatedId;
  const refs = useRef(new Map<string, HTMLButtonElement | null>());
  const listRef = useRef<HTMLDivElement | null>(null);

  // Atualiza os fades laterais conforme a posição do scroll: só marca a borda
  // que tem conteúdo oculto (start = rolou pra direita; end = ainda há à direita).
  const updateScrollFades = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const overflowing = scrollWidth - clientWidth > 1;
    el.dataset.scrollStart = overflowing && scrollLeft > 1 ? 'true' : 'false';
    el.dataset.scrollEnd =
      overflowing && scrollLeft < scrollWidth - clientWidth - 1 ? 'true' : 'false';
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    updateScrollFades();
    el.addEventListener('scroll', updateScrollFades, { passive: true });
    window.addEventListener('resize', updateScrollFades);
    return () => {
      el.removeEventListener('scroll', updateScrollFades);
      window.removeEventListener('resize', updateScrollFades);
    };
  }, [updateScrollFades]);

  // Ao trocar de aba, traz a ativa pra dentro da viewport da lista (mobile scroll).
  useEffect(() => {
    const el = refs.current.get(value);
    el?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
    updateScrollFades();
  }, [value, updateScrollFades]);

  const ids = useMemo(() => {
    const out: string[] = [];
    Children.forEach(children, (child) => {
      if (isValidElement(child)) {
        const el = child as ReactElement<TabProps>;
        if (el.props?.id) out.push(el.props.id);
      }
    });
    return out;
  }, [children]);

  const registerTab = useCallback((id: string, el: HTMLButtonElement | null) => {
    if (el) refs.current.set(id, el);
    else refs.current.delete(id);
  }, []);

  const ctx: TabsContextValue = useMemo(
    () => ({ value, onChange, baseId, ids, registerTab }),
    [value, onChange, baseId, ids, registerTab],
  );

  return (
    <TabsContext.Provider value={ctx}>
      <div ref={listRef} role="tablist" aria-label={label} className={cn(styles.list, className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

Tabs.Tab = Tab;
